import {IPaginateProducts} from 'src/shared/common-dtos/get-all-paginate';
import ApiService from '../../utils/base-api/api-service';
import {ChangeOrderStatusDto} from './dto/change-order-status-dto';
import {OrderDetailsDto} from './dto/order-details-dto';
import {MarkDeliveryFailedDto} from './dto/mark-delivery-failed-dto';
import {OrderDetailsItemsObjDto} from './dto/order-details-items-dto';
import {OrderDetailsItemsParamsDto} from './dto/order-details-items-params-dto';
import {OrderGroupOrderListObjDto} from './dto/order-group-order-list-dto';
import {OrderGrouoOrderListParamsDto} from './dto/order-group-order-list-params-dto';
import {OrdersListObjDto} from './dto/orders-list-dto';
import {OrdersListParamsDto} from './dto/orders-list-params-dto';
import {SetOrderItemAsStockBreakDto} from './dto/set-order-item-stock-break-dto';
import {TrackDetailsDto} from './dto/track-details-dto';
import {ManualDispatchResponseDto} from './dto/manual-dispatch-response-dto';
import {RetryConfirmationResponseDto} from './dto/retry-confirmation-response-dto';
import {MarkReadyResponseDto} from './dto/mark-ready-response-dto';
import {ACTIVE_SHIPPING_CARRIER_CODE} from 'src/shared/constants/AppConst';
import {SubOrdersStatus} from 'src/shared/constants/AppEnums';

class OrdersService extends ApiService {
  constructor() {
    super({
      baseURL: '/OrderAdmin',
    });
  }

  getOrdersList(input: OrdersListParamsDto): Promise<OrdersListObjDto> {
    return this.post('/OrderGroupList', null, {
      params: input,
    });
  }

  pdfAllOrderDocuments() {
    return this.post('/PdfAllOrderDocuments', null, {
      responseType: 'arraybuffer',
    });
  }

  pdfOrderDetails() {
    return this.post('/PdfOrderDetails', null, {
      responseType: 'arraybuffer',
    });
  }

  pdfStockChecklist() {
    return this.post('/PdfStockChecklist', null, {
      responseType: 'arraybuffer',
    });
  }

  pdfOrderInvoiceDocument() {
    return this.post('/PdfOrderInvoiceDocument', null, {
      responseType: 'arraybuffer',
    });
  }

  pdfOrderShipmentDocument() {
    return this.post('/PdfOrderShipmentDocument', null, {
      responseType: 'arraybuffer',
    });
  }

  orderGroupOrderList(
    input: OrderGrouoOrderListParamsDto,
  ): Promise<OrderGroupOrderListObjDto> {
    return this.post('/OrderGroupOrderList', null, {
      params: input,
    });
  }

  changeOrderStatus(input: ChangeOrderStatusDto) {
    return this.post('/ChangeOrderStatus', null, {
      params: input,
      skipErrorNotification: true,
    });
  }

  acceptOrder(orderId: string) {
    return this.post('/AcceptOrder', null, {params: {orderId}});
  }

  rejectOrder(orderId: string, reason: string) {
    return this.post('/RejectOrder', null, {params: {orderId, reason}});
  }

  markReady(orderId: string): Promise<MarkReadyResponseDto> {
    return this.post('/MarkOrderReady', null, {params: {orderId}});
  }

  retryShipmentConfirmation(
    orderId: string,
  ): Promise<RetryConfirmationResponseDto> {
    return this.post('/RetryShipmentConfirmation', null, {params: {orderId}});
  }

  manualDispatchOrder(orderId: string): Promise<ManualDispatchResponseDto> {
    return this.post('/ManualDispatchOrder', null, {params: {orderId}});
  }

  rebookShipment(orderId: string, carrier: string) {
    return this.post('/RebookShipment', null, {params: {orderId, carrier}});
  }

  clearSignOff(orderId: string) {
    return this.post('/ClearSignOff', null, {params: {orderId}});
  }

  getOrdersNeedingSignOff(
    input: IPaginateProducts,
    selectedStatus?: number,
  ): Promise<OrderGroupOrderListObjDto> {
    return this.post('/List', null, {
      params: {
        ...input,
        SelectedStatus: selectedStatus ?? 0,
        NeedsSignOffOnly: true,
      },
    });
  }

  getOrderDetails(orderId: string): Promise<OrderDetailsDto> {
    return this.get('/Details', {params: {id: orderId}});
  }

  getOrderDetailsItems(
    input: OrderDetailsItemsParamsDto,
  ): Promise<OrderDetailsItemsObjDto> {
    return this.get('/OrderItemsList', {params: input});
  }

  setOrderItemAsStockBreak(input: SetOrderItemAsStockBreakDto) {
    return this.post('/SetOrderItemAsStockBreak', null, {
      params: input,
    });
  }

  orderListForShop(
    input: IPaginateProducts,
    selectedStatus: number,
    needsSignOffOnly?: boolean,
  ): Promise<OrderGroupOrderListObjDto> {
    return this.post('/List', null, {
      params: {
        ...input,
        SelectedStatus: selectedStatus,
        ...(needsSignOffOnly ? {NeedsSignOffOnly: true} : {}),
      },
    });
  }

  track(orderId: string): Promise<TrackDetailsDto> {
    return this.post('/Track', null, {
      params: {
        orderId: orderId,
      },
    });
  }

  markDeliveryFailed(input: MarkDeliveryFailedDto) {
    return this.post('/MarkDeliveryFailed', null, {
      params: {
        orderId: input.orderId,
        reasonId: input.reasonId,
        ...(input.noteAr ? {noteAr: input.noteAr} : {}),
        ...(input.noteEn ? {noteEn: input.noteEn} : {}),
      },
      skipErrorNotification: true,
    });
  }
}

export const ordersService = new OrdersService();
